import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1658604663578-04634f4cb897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxjbGluaWMlMjBhYnN0cmFjdCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgxNTY4NjEwfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const resetToken = params.get("reset");   // legacy long-token deep-link (still supported)
  const setupToken = params.get("setup");

  useEffect(() => {
    if (params.get("revoked") === "1") {
      toast.error("Your session was ended by an admin. Please sign in again if your access has been restored.");
    }
  }, [params]);

  const handle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/provider");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = (role) => {
    if (role === "admin") { setEmail("admin@oppo.com"); setPassword("Admin@123"); }
    else { setEmail("provider@oppo.com"); setPassword("Provider@123"); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="flex lg:w-1/2 bg-brand-navy text-white relative overflow-hidden min-h-[42vh] lg:min-h-screen">
        <div className="relative z-10 px-12 pt-10 pb-10 flex flex-col h-full w-full">
          {/* Mountain emblem near the top, then centered title cluster + tagline below */}
          <div className="flex flex-col items-center text-center px-4 flex-1 justify-center -mt-10">
            <img
              src="/brand/oppo-mountain.png"
              alt="OPPO — mountain-peak emblem"
              className="block w-full max-w-md mb-10 select-none pointer-events-none"
              draggable={false}
              data-testid="login-brand-emblem"
            />
            <h1
              className="font-display font-bold tracking-tight leading-[1.1] text-white"
              data-testid="login-title"
            >
              <span className="block text-3xl sm:text-4xl xl:text-5xl">OPPO, LLC.</span>
              <span className="block text-2xl sm:text-3xl xl:text-4xl font-semibold mt-3 text-white/95">
                Host Home Contractor Portal
              </span>
            </h1>
            <p
              className="mt-6 text-base sm:text-lg xl:text-xl font-medium italic text-white opacity-95 tracking-wide max-w-md"
              data-testid="login-tagline"
            >
              Creating Safe, Happy &amp; Healthy Opportunities!
            </p>
          </div>

          <p className="text-xs opacity-70 mt-auto">© OPPO, LLC. All rights reserved.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm space-y-7">
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navy" data-testid="welcome-heading">
              Welcome back
            </h2>
          </div>
          <form onSubmit={handle} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw">Password</Label>
              <Input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12"
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base"
              disabled={submitting}
              data-testid="login-submit"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <div className="border-t border-border pt-4 space-y-2">
            <ForgotPasswordDialog defaultEmail={email} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordDialog({ defaultEmail }) {
  // v33 — Clean 2-step OTP flow:
  //   step 'request': enter email, click "Send code" → backend issues a 6-digit OTP
  //   step 'verify':  enter the 6-digit code + new password, click "Set password"
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState(defaultEmail || "");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [devCode, setDevCode] = useState("");   // shown in non-prod environments only

  const reset = () => {
    setStep("request");
    setCode("");
    setNewPw("");
    setConfirmPw("");
    setDevCode("");
    setSubmitting(false);
  };

  const requestCode = async () => {
    if (!email.trim()) return toast.error("Enter your email");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      // Backend always returns {ok:true}. In dev it also returns dev_code so the
      // tester can complete the flow without an outbound email integration.
      if (data?.dev_code) setDevCode(data.dev_code);
      setStep("verify");
      toast.success("If that email is on file, a 6-digit code has been issued.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not request code");
    } finally { setSubmitting(false); }
  };

  const verifyAndReset = async () => {
    if (!/^\d{6}$/.test(code.trim())) return toast.error("Enter the 6-digit code");
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPw,
      });
      toast.success("Password updated. You can sign in now.");
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button type="button" variant="link" size="sm" className="px-0" data-testid="open-forgot">
          Forgot password?
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            {step === "request"
              ? "Enter the email on your account. We'll send you a 6-digit code."
              : "Enter the 6-digit code we sent and choose a new password."}
          </DialogDescription>
        </DialogHeader>

        {step === "request" && (
          <div className="space-y-3" data-testid="forgot-step-request">
            <div className="space-y-1.5">
              <Label htmlFor="forgot-email-input">Email</Label>
              <Input
                id="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                data-testid="forgot-email"
              />
            </div>
            <DialogFooter>
              <Button onClick={requestCode} disabled={submitting} data-testid="forgot-submit">
                {submitting ? "Sending…" : "Send code"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-3" data-testid="forgot-step-verify">
            {devCode && (
              <div className="rounded-md border-2 border-amber-200 bg-amber-50 p-2 text-[11px]" data-testid="dev-code-banner">
                <span className="font-semibold text-amber-800">Dev mode:</span>{" "}
                your code is{" "}
                <code className="font-mono font-bold tracking-widest" data-testid="dev-code">
                  {devCode}
                </code>{" "}
                — production users receive it by email instead.
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="otp-input">6-digit code</Label>
              <Input
                id="otp-input"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••"
                className="text-center text-2xl font-mono tracking-[0.5em]"
                autoComplete="one-time-code"
                data-testid="forgot-code"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-pw-input">New password</Label>
              <Input
                id="new-pw-input" type="password" value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                data-testid="reset-new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-pw-input">Confirm new password</Label>
              <Input
                id="confirm-pw-input" type="password" value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
                data-testid="reset-confirm-password"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setStep("request")} disabled={submitting}>
                Back
              </Button>
              <Button onClick={verifyAndReset} disabled={submitting} data-testid="reset-submit">
                {submitting ? "Saving…" : "Set new password"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RespiteSetupDialog({ token: defaultToken }) {
  const [open, setOpen] = useState(!!defaultToken);
  const [token, setToken] = useState(defaultToken || "");
  const [newPw, setNewPw] = useState("");
  const submit = async () => {
    if (!newPw || newPw.length < 6) return toast.error("Min 6 characters");
    try {
      const { data } = await api.post("/respite/setup-password", { token, new_password: newPw });
      toast.success(`Password set for ${data.email}. You can sign in now.`);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="link" size="sm" className="px-0" data-testid="open-respite-setup">
          Activate Respite Provider Account
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set up your Respite Provider Password</DialogTitle>
          <DialogDescription>
            Use the secure link sent to your email to create your password and gain temporary access to the Contractor Portal.
          </DialogDescription>
        </DialogHeader>
        <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Setup token" data-testid="respite-setup-token" />
        <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 6 chars)" data-testid="respite-setup-password" />
        <DialogFooter>
          <Button onClick={submit} data-testid="respite-setup-submit">Activate account</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
